/**
 * iter328 — MealTimingOptimizer widget
 *
 * Lets a user enter 2-3 planned meals (name + NIS%) and receive optimal
 * consumption times across the day, accounting for chronotype.
 *
 * Backend: POST /api/plate-simulator/timing-optimizer
 */
import React, { useState } from "react";
import axios from "axios";
import { Clock, Sun, Moon, Sparkles, Loader2, Plus, X, ArrowRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MealTimingOptimizer() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [meals, setMeals] = useState([
    { name: bg ? "Закуска" : "Breakfast", nis_percent: 65, t_peak_min: 35 },
    { name: bg ? "Обяд"    : "Lunch",     nis_percent: 50, t_peak_min: 55 },
  ]);
  const [chronotype, setChronotype] = useState("balanced");
  const [earliest, setEarliest] = useState(7);
  const [latest, setLatest] = useState(21);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const updateMeal = (idx, key, val) => {
    setMeals((prev) => prev.map((m, i) => (i === idx ? { ...m, [key]: val } : m)));
  };

  const addMeal = () => {
    if (meals.length >= 3) return;
    setMeals((prev) => [...prev, { name: bg ? `Хранене ${prev.length + 1}` : `Meal ${prev.length + 1}`, nis_percent: 40, t_peak_min: 60 }]);
  };

  const removeMeal = (idx) => {
    if (meals.length <= 1) return;
    setMeals((prev) => prev.filter((_, i) => i !== idx));
  };

  const optimize = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/plate-simulator/timing-optimizer`, {
        meals,
        chronotype,
        earliest_meal_hour: earliest,
        latest_meal_hour: latest,
        language,
      });
      setResult(data);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error("Timing optimizer error:", e);
    } finally {
      setLoading(false);
    }
  };

  const cardCls = isDark ? "bg-[hsl(223,47%,12%)] border border-white/5" : "bg-white border border-gray-100 shadow-sm";

  return (
    <div className={`rounded-2xl p-4 space-y-3 ${cardCls}`} data-testid="meal-timing-optimizer">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 flex items-center justify-center text-white">
          <Clock className="w-4 h-4" />
        </div>
        <div>
          <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            {bg ? "Оптимизатор на времето на хранене" : "Meal Timing Optimizer"}
          </h3>
          <p className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            {bg
              ? "Препоръчва най-добрите часове за 2-3 хранения според NIS% и хронотип"
              : "Recommends optimal times for 2-3 meals based on NIS% and chronotype"}
          </p>
        </div>
      </div>

      {/* Meals input */}
      <div className="space-y-2">
        {meals.map((m, idx) => (
          <div key={idx} className={`grid grid-cols-12 gap-2 items-center p-2 rounded-lg ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
            <input
              type="text"
              value={m.name}
              onChange={(e) => updateMeal(idx, "name", e.target.value)}
              className={`col-span-5 px-2 py-1.5 rounded text-xs ${isDark ? "bg-white/5 text-white border border-white/10" : "bg-white text-gray-800 border border-gray-200"}`}
              placeholder={bg ? "Име" : "Name"}
              data-testid={`mto-meal-name-${idx}`}
            />
            <div className="col-span-3">
              <input
                type="number"
                min={0}
                max={100}
                value={m.nis_percent}
                onChange={(e) => updateMeal(idx, "nis_percent", parseFloat(e.target.value) || 0)}
                className={`w-full px-2 py-1.5 rounded text-xs ${isDark ? "bg-white/5 text-white border border-white/10" : "bg-white text-gray-800 border border-gray-200"}`}
                placeholder="NIS"
                data-testid={`mto-meal-nis-${idx}`}
              />
              <span className="text-[9px] opacity-60 ml-1">NIS%</span>
            </div>
            <div className="col-span-3">
              <input
                type="number"
                min={10}
                max={180}
                step={5}
                value={m.t_peak_min}
                onChange={(e) => updateMeal(idx, "t_peak_min", parseInt(e.target.value, 10) || 45)}
                className={`w-full px-2 py-1.5 rounded text-xs ${isDark ? "bg-white/5 text-white border border-white/10" : "bg-white text-gray-800 border border-gray-200"}`}
                placeholder="Peak"
                data-testid={`mto-meal-tpeak-${idx}`}
              />
              <span className="text-[9px] opacity-60 ml-1">{bg ? "Пик min" : "Peak min"}</span>
            </div>
            <button
              onClick={() => removeMeal(idx)}
              disabled={meals.length <= 1}
              className={`col-span-1 flex items-center justify-center w-7 h-7 rounded ${isDark ? "hover:bg-white/10" : "hover:bg-gray-200"} disabled:opacity-30`}
              aria-label={bg ? "Премахни" : "Remove"}
              data-testid={`mto-remove-${idx}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {meals.length < 3 && (
          <button
            onClick={addMeal}
            className={`w-full inline-flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-semibold border-2 border-dashed ${isDark ? "border-white/10 text-gray-300 hover:border-white/20" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
            data-testid="mto-add-meal"
          >
            <Plus className="w-3.5 h-3.5" />
            {bg ? "Добави хранене" : "Add meal"}
          </button>
        )}
      </div>

      {/* Settings */}
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div>
          <label className={`block mb-1 text-[10px] uppercase tracking-wide opacity-70 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            {bg ? "Хронотип" : "Chronotype"}
          </label>
          <select
            value={chronotype}
            onChange={(e) => setChronotype(e.target.value)}
            className={`w-full px-2 py-1.5 rounded text-xs ${isDark ? "bg-white/5 text-white border border-white/10" : "bg-white text-gray-800 border border-gray-200"}`}
            data-testid="mto-chronotype"
          >
            <option value="lark">{bg ? "Чучулига (07-10)" : "Lark (07-10)"}</option>
            <option value="balanced">{bg ? "Балансиран" : "Balanced"}</option>
            <option value="owl">{bg ? "Сова (18-21)" : "Owl (18-21)"}</option>
          </select>
        </div>
        <div>
          <label className={`block mb-1 text-[10px] uppercase tracking-wide opacity-70 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            {bg ? "Най-рано" : "Earliest"}
          </label>
          <input
            type="number" min={4} max={11} value={earliest}
            onChange={(e) => setEarliest(parseInt(e.target.value, 10) || 7)}
            className={`w-full px-2 py-1.5 rounded text-xs ${isDark ? "bg-white/5 text-white border border-white/10" : "bg-white text-gray-800 border border-gray-200"}`}
            data-testid="mto-earliest"
          />
        </div>
        <div>
          <label className={`block mb-1 text-[10px] uppercase tracking-wide opacity-70 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            {bg ? "Най-късно" : "Latest"}
          </label>
          <input
            type="number" min={17} max={23} value={latest}
            onChange={(e) => setLatest(parseInt(e.target.value, 10) || 21)}
            className={`w-full px-2 py-1.5 rounded text-xs ${isDark ? "bg-white/5 text-white border border-white/10" : "bg-white text-gray-800 border border-gray-200"}`}
            data-testid="mto-latest"
          />
        </div>
      </div>

      {/* Optimize button */}
      <button
        onClick={optimize}
        disabled={loading || meals.length < 1}
        className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white text-sm font-bold shadow hover:shadow-md transition-shadow disabled:opacity-50"
        data-testid="mto-optimize-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        {bg ? "Изчисли оптимални часове" : "Compute optimal times"}
      </button>

      {/* Results */}
      {result && (
        <div className="space-y-2 pt-2 border-t border-white/5" data-testid="mto-result">
          {result.recommendations.map((r, idx) => (
            <div key={idx} className={`flex items-start gap-3 p-2.5 rounded-lg ${isDark ? "bg-white/5" : "bg-gray-50"}`} data-testid={`mto-rec-${idx}`}>
              <div className="flex-shrink-0 w-14 h-14 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex flex-col items-center justify-center text-white">
                <Clock className="w-3.5 h-3.5 mb-0.5" />
                <span className="text-[11px] font-bold">{r.recommended_clock}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-800"}`}>{r.name}</span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-600">
                    NIS {Math.round(r.nis_percent)}%
                  </span>
                </div>
                <p className={`text-[11px] leading-snug mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  {bg ? r.rationale_bg : r.rationale_en}
                </p>
              </div>
            </div>
          ))}
          <div className={`flex items-center justify-between text-[10px] px-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            <span>{bg ? "Общ дневен AUC:" : "Total daily AUC:"} <strong>{Math.round(result.total_daily_auc).toLocaleString()}</strong></span>
            <span>{bg ? "Припокриване:" : "Overlap:"} {result.total_dip_overlap_minutes} min</span>
          </div>
          <p className={`text-[10px] leading-tight ${isDark ? "text-gray-500" : "text-gray-400"}`}>
            {bg ? result.summary_bg : result.summary_en}
          </p>
        </div>
      )}
    </div>
  );
}
